import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CustomScriptService } from '../../../ajs-upgraded-providers';
import { DealerService } from '../../../services/dealer.service';
import { StorageService } from '../../../modules/storage/storage.service';
import { Subscription, from } from 'rxjs';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { alertScriptTypes } from '../../../constants/alert-script-types';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AdminCustomScriptsEditModalComponent } from './admin-custom-scripts-edit-modal.component';

interface ScriptOption {
    id: number;
    labelKey: string;
}

interface LanguageOption {
    value: string;
    labelKey: string;
}

interface CustomScriptItem {
    id?: number;
    dealerID: number;
    scriptTypeID: number;
    culture: string;
    version: number;
    createDate?: string;
    createdBy?: number | string;
    createdByFullName?: string;
    scriptBody: string;
    scriptTypeLabel?: string;
    languageLabel?: string;
}

@Component({
    selector: 'admin-custom-scripts',
    templateUrl: './admin-custom-scripts.component-ng.html',
    styleUrls: ['./admin-custom-scripts.component.scss']
})
export class AdminCustomScriptsComponent implements OnInit, OnDestroy {
    public scriptTypes: ScriptOption[] = [];

    public languages: LanguageOption[] = [];

    public dealers: Array<any> = [];
    public selectedDealerId: number;
    public selectedScriptTypeId: number | null;
    public selectedLanguage: string | null;
    public busy: Subscription;
    public customScripts: CustomScriptItem[] = [];
    public gridView: GridDataResult;
    public pageSize = 20;
    public skip = 0;
    public sort: SortDescriptor[] = [{
        field: 'scriptTypeLabel',
        dir: 'asc'
    }];
    public bsModalRef: BsModalRef | null;
    private modalHiddenSubscription: Subscription | null;
    private subscriptionAggregator = new Subscription();
    private scriptTypeLabelKeys: Record<string, string> = {
        alert: 'alertScriptLabel',
        alertCrossMakeUsed: 'alertCrossMakeUsedScriptLabel',
        contractEnd: 'contractEndScriptLabel',
        leaseEnd: 'leaseEndScriptLabel',
        mileage: 'mileageScriptLabel',
        inMarket: 'inMarketScriptLabel',
        inMarketEngaged: 'inMarketEngagedScriptLabel',
        warranty: 'warrantyScriptLabel',
        phone: 'phoneScriptLabel',
        conquest: 'conquestScriptLabel',
        cash: 'cashScriptLabel',
        appointment: 'appointmentScriptLabel',
        service: 'serviceScriptLabel',
        privateOffer: 'privateOfferScriptLabel',
        privateOfferWithPreApproval: 'privateOfferWithPreApprovalScriptLabel'
    };

    constructor(
        public tService: TranslateService,
        private dealerService: DealerService,
        private storageService: StorageService,
        private modalService: BsModalService,
        @Inject(CustomScriptService) private customScriptService: any
    ) { }

    ngOnInit(): void {
        this.dealers = this.dealerService.getSelectedDealers();
        const dealerId = this.storageService.getItem('selectedDealerId');
        this.selectedDealerId = dealerId > 0 ? dealerId : this.dealers[0].id;
        this.scriptTypes = this.buildScriptTypeOptions();
        this.selectedScriptTypeId = this.scriptTypes.length ? this.scriptTypes[0].id : null;
        this.selectedLanguage = this.languages.length ? this.languages[0].value : null;
        this.loadScripts();
    }

    ngOnDestroy(): void {
        this.subscriptionAggregator.unsubscribe();
        if (this.modalHiddenSubscription) {
            this.modalHiddenSubscription.unsubscribe();
            this.modalHiddenSubscription = null;
        }
    }

    public onDealerSelect(): void {
        this.storageService.setItem('selectedDealerId', this.selectedDealerId);
        this.loadScripts();
    }

    public onFilterChange(): void {
        this.skip = 0;
        this.refreshGridView();
    }

    public pageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.refreshGridView();
    }

    public sortChange(sort: SortDescriptor[]): void {
        if (sort[0].field === this.sort[0].field) {
            sort[0].dir = (this.sort[0].dir === 'asc') ? 'desc' : 'asc';
        }
        this.sort = sort;
        this.refreshGridView();
    }

    public newScript(): void {
        this.openEditModal(0);
    }

    public editScriptItem(script: CustomScriptItem): void {
        this.openEditModal(script.id);
    }

    private loadScripts(): void {
        this.busy = from(this.customScriptService.getCustomScriptsByDealerId(this.selectedDealerId))
            .subscribe((scripts: CustomScriptItem[]) => {
                const rawScripts = scripts || [];
                this.languages = this.buildLanguageOptions(rawScripts);
                const selectedMatch = this.languages.find(language => language.value === this.selectedLanguage);
                if (!selectedMatch) {
                    this.selectedLanguage = this.languages.length ? this.languages[0].value : null;
                }
                this.customScripts = rawScripts.map(script => this.decorateScript(script));
                this.customScripts = orderBy(this.customScripts, this.sort);
                this.refreshGridView();
            });

        this.subscriptionAggregator.add(this.busy);
    }

    private refreshGridView(): void {
        this.customScripts = orderBy(this.customScripts, this.sort);
        const filteredScripts = this.getFilteredScripts();
        this.gridView = {
            data: filteredScripts.slice(this.skip, this.skip + this.pageSize),
            total: filteredScripts.length
        };
    }

    private decorateScript(script: CustomScriptItem): CustomScriptItem {
        return Object.assign({}, script, {
            scriptTypeLabel: this.getScriptTypeLabel(script.scriptTypeID),
            languageLabel: this.getLanguageLabel(script.culture)
        });
    }

    private getScriptTypeLabel(scriptTypeId: number): string {
        const match = this.scriptTypes.find(type => type.id === scriptTypeId);
        return match ? this.tService.instant(match.labelKey) : '';
    }

    private getLanguageLabel(culture: string): string {
        const match = this.languages.find(language => language.value === culture);
        return match ? this.tService.instant(match.labelKey) : culture;
    }

    private buildLanguageOptions(scripts: CustomScriptItem[]): LanguageOption[] {
        const knownLabels: Record<string, string> = {
            'en-US': 'adminCustomScripts_language_english',
            'fr-CA': 'adminCustomScripts_language_frenchCanadian',
            'es-US': 'adminCustomScripts_language_spanish'
        };
        const seen = new Set<string>();
        const ordered: LanguageOption[] = [];

        scripts.forEach(script => {
            const culture = script.culture;
            if (!culture || seen.has(culture)) {
                return;
            }
            seen.add(culture);
            ordered.push({
                value: culture,
                labelKey: knownLabels[culture] || culture
            });
        });

        const byValue = ordered.reduce((acc, option) => {
            acc[option.value] = option;
            return acc;
        }, {} as Record<string, LanguageOption>);

        if (byValue['en-US'] && byValue['es-US']) {
            return [byValue['en-US'], byValue['es-US']];
        }
        if (byValue['en-US'] && byValue['fr-CA']) {
            return [byValue['en-US'], byValue['fr-CA']];
        }
        if (byValue['en-CA'] && byValue['fr-CA']) {
            return [byValue['en-CA'], byValue['fr-CA']];
        }

        return ordered.slice(0, 2);
    }

    private getFilteredScripts(): CustomScriptItem[] {
        return this.customScripts.filter(script => {
            const matchesLanguage = this.selectedLanguage
                ? (script.culture || '').toLowerCase() === this.selectedLanguage.toLowerCase()
                : true;
            return matchesLanguage;
        });
    }

    private buildScriptTypeOptions(): ScriptOption[] {
        return Object.keys(alertScriptTypes).map(key => ({
            id: (alertScriptTypes as any)[key],
            labelKey: this.scriptTypeLabelKeys[key] || key
        }));
    }

    private openEditModal(id: number | undefined): void {
        const initialState = {
            id: id || 0,
            dealerId: this.selectedDealerId,
            scriptTypeId: this.selectedScriptTypeId,
            culture: this.selectedLanguage
        };

        this.bsModalRef = this.modalService.show(AdminCustomScriptsEditModalComponent, { initialState: initialState, class: 'modal-lg' });
        if (this.modalHiddenSubscription) {
            this.modalHiddenSubscription.unsubscribe();
        }
        this.modalHiddenSubscription = this.modalService.onHidden.subscribe(() => {
            this.loadScripts();
            if (this.modalHiddenSubscription) {
                this.modalHiddenSubscription.unsubscribe();
                this.modalHiddenSubscription = null;
            }
            this.bsModalRef = null;
        });
    }
}
